import type { Credential, CredentialAccessLog, CredentialSecret } from '@pm/schemas';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  Input,
  LabelCaps,
  Markdown,
  MarkdownEditor,
  Skeleton,
  SkeletonRows,
  Textarea,
} from '@pm/ui';
import {
  Copy,
  Eye,
  EyeOff,
  History,
  KeyRound,
  Lock,
  Pencil,
  Plus,
  Save,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDateTime } from '@/lib/format';
import { comKeys, useContextNote, useCredentials, useInvalidating } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { copyText } from '@/features/forms/form-bits';

/** SPEC §29 — notas Markdown do contexto. Senhas vão no cofre (RULE-041). */
export function NotesSection({ contextId }: { contextId: string }) {
  const toast = useToast();
  const { can } = useAuth();
  const { data, isLoading } = useContextNote(contextId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const save = useInvalidating(
    (content: string) => api.put(`/contexts/${contextId}/notes`, { content_markdown: content }),
    [comKeys.note(contextId)],
  );
  if (isLoading || !data) return <Skeleton className="h-40 rounded-md" />;
  return (
    <section className="flex flex-col gap-3 rounded-md border border-border bg-surface-1 p-4">
      <header className="flex items-center gap-2">
        <LabelCaps>Notas</LabelCaps>
        {data.updated_at ? (
          <span className="font-mono text-xs text-text-3">
            atualizado {fmtDateTime(data.updated_at)}
            {data.updated_by ? ` · ${data.updated_by.name}` : ''}
          </span>
        ) : null}
        {can('clients.edit') ? (
          editing ? (
            <span className="ml-auto flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={save.isPending}
                onClick={() =>
                  save.mutate(draft, {
                    onSuccess: () => (toast.success('Notas salvas'), setEditing(false)),
                    onError: (e) => toast.error(e),
                  })
                }
              >
                <Save /> Salvar
              </Button>
            </span>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={() => {
                setDraft(data.content_markdown);
                setEditing(true);
              }}
            >
              <Pencil /> Editar
            </Button>
          )
        ) : null}
      </header>
      <p className="flex items-center gap-1.5 text-xs text-text-3">
        <Lock className="size-3" /> Não guarde senhas aqui: use o Cofre, que cifra e audita cada
        acesso.
      </p>
      {editing ? (
        <MarkdownEditor value={draft} onChange={setDraft} minRows={10} autoFocus />
      ) : data.content_markdown ? (
        <Markdown
          onChange={
            can('clients.edit')
              ? (next) => save.mutate(next, { onError: (e) => toast.error(e) })
              : undefined
          }
        >
          {data.content_markdown}
        </Markdown>
      ) : (
        <p className="text-sm text-text-3">
          Sem notas. Briefing, acessos gerais, combinados com o cliente…
        </p>
      )}
    </section>
  );
}

/** SPEC §30 / RULE-042 — cofre: listagem sem senha, revelação e cópia auditadas. */
export function VaultSection({ contextId }: { contextId: string }) {
  const toast = useToast();
  const { can } = useAuth();
  const canView = can('clients.view_credentials');
  const canManage = can('clients.manage_credentials');
  const { data = [], isLoading, isError, error } = useCredentials(contextId, canView);
  const [editing, setEditing] = useState<Credential | 'new' | null>(null);
  const [remove, setRemove] = useState<Credential | null>(null);
  const [log, setLog] = useState<Credential | null>(null);
  const del = useInvalidating(
    (id: string) => api.delete(`/credentials/${id}`),
    [comKeys.credentials(contextId)],
  );

  if (!canView)
    return (
      <EmptyState
        icon={Lock}
        title="Cofre restrito"
        description="Você não tem permissão para ver credenciais deste cliente."
      />
    );
  return (
    <div className="flex flex-col gap-gap">
      <div className="flex items-center gap-2">
        <p className="text-xs text-text-3">
          Segredos cifrados em repouso (AES-256-GCM). Revelar ou copiar fica no audit log com
          usuário, horário e IP.
        </p>
        {canManage ? (
          <Button variant="primary" size="sm" className="ml-auto" onClick={() => setEditing('new')}>
            <Plus /> Credencial
          </Button>
        ) : null}
      </div>
      <div className="rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows rows={3} />
        ) : isError ? (
          <p className="p-4 text-sm text-danger">{error.message}</p>
        ) : data.length === 0 ? (
          <EmptyState
            icon={KeyRound}
            title="Cofre vazio"
            description="Guarde aqui acessos a plataformas do cliente (Meta, Google, hospedagem…)."
          />
        ) : (
          <ul>
            {data.map((c) => (
              <CredentialRow
                key={c.id}
                cred={c}
                canManage={canManage}
                onEdit={() => setEditing(c)}
                onRemove={() => setRemove(c)}
                onLog={() => setLog(c)}
              />
            ))}
          </ul>
        )}
      </div>
      {editing ? (
        <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent
            title={editing === 'new' ? 'Nova credencial' : 'Editar credencial'}
            size="sm"
          >
            <CredentialForm
              contextId={contextId}
              cred={editing === 'new' ? null : editing}
              onDone={() => setEditing(null)}
            />
          </DialogContent>
        </Dialog>
      ) : null}
      {log ? (
        <Dialog open onOpenChange={(o) => !o && setLog(null)}>
          <DialogContent title={`Acessos · ${log.service_name}`} size="sm">
            <AccessLog credentialId={log.id} />
          </DialogContent>
        </Dialog>
      ) : null}
      <ConfirmDialog
        open={remove !== null}
        onOpenChange={() => setRemove(null)}
        title={`Excluir ${remove?.service_name ?? ''}?`}
        description="A credencial vai para a lixeira; o audit log é mantido."
        confirmLabel="Excluir"
        danger
        loading={del.isPending}
        onConfirm={() =>
          remove &&
          del.mutate(remove.id, {
            onSuccess: () => (toast.success('Credencial excluída'), setRemove(null)),
            onError: (e) => toast.error(e),
          })
        }
      />
    </div>
  );
}

function CredentialRow({
  cred,
  canManage,
  onEdit,
  onRemove,
  onLog,
}: {
  cred: Credential;
  canManage: boolean;
  onEdit: () => void;
  onRemove: () => void;
  onLog: () => void;
}) {
  const toast = useToast();
  const [secret, setSecret] = useState<CredentialSecret | null>(null);
  const reveal = useInvalidating(
    () => api.post<CredentialSecret>(`/credentials/${cred.id}/reveal`),
    [comKeys.credentials(cred.context_id)],
  );
  const copied = useInvalidating(
    (field: 'username' | 'password') => api.post(`/credentials/${cred.id}/copied`, { field }),
    [],
  );
  const copy = async (field: 'username' | 'password') => {
    let value = field === 'username' ? cred.username : secret?.password;
    if (field === 'password' && !value) {
      const s = await api
        .post<CredentialSecret>(`/credentials/${cred.id}/reveal`)
        .catch((e: unknown) => (toast.error(e), null));
      if (!s) return;
      value = s.password;
    }
    if (!value) return;
    const ok = await copyText(value);
    if (ok) {
      toast.success('Copiado');
      copied.mutate(field);
    } else toast.error('Não foi possível copiar');
  };
  return (
    <li className="flex flex-col gap-2 border-b border-border px-4 py-3 last:border-b-0">
      <div className="flex items-center gap-3">
        <KeyRound className="size-4 text-text-3" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">
            {cred.service_name}
            {cred.url ? (
              <a
                href={cred.url}
                target="_blank"
                rel="noreferrer"
                className="ml-2 font-mono text-xs text-accent hover:underline"
              >
                {cred.url.replace(/^https?:\/\//, '')}
              </a>
            ) : null}
          </p>
          <p className="flex items-center gap-2 font-mono text-xs text-text-2">
            {cred.username ? (
              <>
                <span>{cred.username}</span>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="size-5"
                  aria-label="Copiar usuário"
                  onClick={() => void copy('username')}
                >
                  <Copy />
                </Button>
              </>
            ) : (
              <span className="text-text-3">sem usuário</span>
            )}
          </p>
        </div>
        {cred.has_password ? (
          secret ? (
            <Button variant="ghost" size="sm" onClick={() => setSecret(null)}>
              <EyeOff /> Ocultar
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              loading={reveal.isPending}
              onClick={() =>
                reveal.mutate(undefined, { onSuccess: setSecret, onError: (e) => toast.error(e) })
              }
            >
              <Eye /> Revelar
            </Button>
          )
        ) : (
          <Badge tone="neutral">sem senha</Badge>
        )}
        {cred.has_password ? (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Copiar senha"
            onClick={() => void copy('password')}
          >
            <Copy />
          </Button>
        ) : null}
        {canManage ? (
          <>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Histórico de acessos"
              onClick={onLog}
            >
              <History />
            </Button>
            <Button variant="ghost" size="icon-sm" aria-label="Editar" onClick={onEdit}>
              <Pencil />
            </Button>
            <Button variant="ghost" size="icon-sm" aria-label="Excluir" onClick={onRemove}>
              <Trash2 />
            </Button>
          </>
        ) : null}
      </div>
      {secret ? (
        <div className="ml-7 grid gap-1 rounded border border-warning/40 bg-warning/5 px-3 py-2 font-mono text-xs">
          <span>
            <span className="text-text-3">senha </span>
            {secret.password ?? '—'}
          </span>
          {secret.notes ? (
            <span className="whitespace-pre-wrap">
              <span className="text-text-3">obs. </span>
              {secret.notes}
            </span>
          ) : null}
          <span className="text-[10px] text-text-3">Revelação registrada no audit log.</span>
        </div>
      ) : null}
      {cred.last_revealed_at ? (
        <p className="ml-7 font-mono text-[10px] text-text-3">
          última revelação {fmtDateTime(cred.last_revealed_at)}
          {cred.last_revealed_by ? ` · ${cred.last_revealed_by.name}` : ''}
        </p>
      ) : null}
    </li>
  );
}

function CredentialForm({
  contextId,
  cred,
  onDone,
}: {
  contextId: string;
  cred: Credential | null;
  onDone: () => void;
}) {
  const toast = useToast();
  const [service, setService] = useState(cred?.service_name ?? '');
  const [url, setUrl] = useState(cred?.url ?? '');
  const [username, setUsername] = useState(cred?.username ?? '');
  const [password, setPassword] = useState('');
  const [notes, setNotes] = useState('');
  const [show, setShow] = useState(false);
  const save = useInvalidating(
    (input: Record<string, unknown>) =>
      cred
        ? api.patch(`/credentials/${cred.id}`, input)
        : api.post(`/contexts/${contextId}/credentials`, input),
    [comKeys.credentials(contextId)],
  );
  return (
    <form
      className="flex flex-col gap-3"
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        const input: Record<string, unknown> = {
          service_name: service,
          url: url || null,
          username: username || null,
        };
        if (!cred || password) input.password = password || null;
        if (!cred || notes) input.notes = notes || null;
        save.mutate(input, {
          onSuccess: () => (toast.success('Credencial salva'), onDone()),
          onError: (err) => toast.error(err),
        });
      }}
    >
      <Field label="Serviço" htmlFor="cr-svc">
        <Input
          id="cr-svc"
          autoFocus
          value={service}
          onChange={(e) => setService(e.target.value)}
          placeholder="Meta Business, Google Ads, Hostinger…"
        />
      </Field>
      <Field label="URL" htmlFor="cr-url">
        <Input
          id="cr-url"
          type="url"
          mono
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://"
        />
      </Field>
      <Field label="Usuário" htmlFor="cr-user">
        <Input
          id="cr-user"
          mono
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="off"
        />
      </Field>
      <Field label={cred ? 'Nova senha (vazio mantém a atual)' : 'Senha'} htmlFor="cr-pass">
        <div className="flex gap-2">
          <Input
            id="cr-pass"
            type={show ? 'text' : 'password'}
            mono
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={show ? 'Ocultar' : 'Mostrar'}
            onClick={() => setShow((v) => !v)}
          >
            {show ? <EyeOff /> : <Eye />}
          </Button>
        </div>
      </Field>
      <Field
        label={cred ? 'Observações (vazio mantém as atuais)' : 'Observações (cifradas)'}
        htmlFor="cr-notes"
      >
        <Textarea
          id="cr-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="min-h-[56px]"
          placeholder="MFA, códigos de recuperação, dono do acesso…"
        />
      </Field>
      <DialogFooter>
        <Button variant="ghost" type="button" onClick={onDone}>
          Cancelar
        </Button>
        <Button variant="primary" type="submit" loading={save.isPending} disabled={!service.trim()}>
          Salvar
        </Button>
      </DialogFooter>
    </form>
  );
}

const LOG_LABEL: Record<CredentialAccessLog['action'], string> = {
  'credentials.reveal': 'revelou a senha',
  'credentials.copy': 'copiou',
  'credentials.create': 'criou',
  'credentials.update': 'editou',
  'credentials.delete': 'excluiu',
};

function AccessLog({ credentialId }: { credentialId: string }) {
  const [rows, setRows] = useState<CredentialAccessLog[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (rows === null && error === null) {
    void api
      .get<CredentialAccessLog[]>(`/credentials/${credentialId}/access-log`)
      .then(setRows, (e: Error) => setError(e.message));
  }
  if (error) return <p className="text-sm text-danger">{error}</p>;
  if (!rows) return <SkeletonRows rows={3} />;
  return (
    <ul className="max-h-[360px] overflow-y-auto">
      {rows.map((r) => (
        <li
          key={r.id}
          className="flex items-center gap-2 border-b border-border py-1.5 text-sm last:border-b-0"
        >
          <span className="font-medium">{r.actor?.name ?? 'sistema'}</span>
          <span className="text-text-2">{LOG_LABEL[r.action]}</span>
          <span className="ml-auto font-mono text-xs text-text-3">
            {fmtDateTime(r.created_at)}
            {r.ip ? ` · ${r.ip}` : ''}
          </span>
        </li>
      ))}
      {rows.length === 0 ? (
        <li className="py-4 text-center text-sm text-text-3">Sem acessos registrados.</li>
      ) : null}
    </ul>
  );
}
